import React, { FC, useState } from 'react'
import { LoanPurpose, Rate, Section } from '@mortgage-pos/types'
import { useStoreState, useStoreActions } from '@mortgage-pos/ui/store'

import {
  LoanCostsCalculator,
  extractFeeByDescription,
  formatNumberToDollarAmount,
} from '@mortgage-pos/utils'

import Tooltip from './Tooltip'
import { Modal } from './Modal'
import InfoBlock from './InfoBlock'
import CloseIcon from '@mortgage-pos/ui/assets/black-x.svg'

interface Props {
  onClose: () => void
  rate: Rate
}

const TabbedFeeEstimateModal: FC<Props> = ({ onClose, rate }) => {
  const { mergedAnswers } = useStoreState((state) => state.answers)
  const { elementClicked } = useStoreActions((state) => state.tracking)

  const [activeTab, setActiveTab] = useState('cashToClose')

  const handleTabClick = (tab: string) => {
    setActiveTab(tab)
    elementClicked({
      elementType: 'Button',
      location: 'Tabbed Fee Estimate Modal',
      actionOutcome: 'Shows fees for section: ' + tab,
      text: tab,
    })
  }
  const {
    cashOut,
    escrowRollIn,
    remainingBalance,
    escrow: waiveEscrow,
    // Aliased var name since rollInFees is a Yes/No boolean here
    // but is a number amt in other places in our ecosystem (namely backend
    // systems to show the dollar amt being rolled in)
    rollInFees: willRollInFees,
    propertyDownPayment,
    propertyPurchasePrice,
  } = mergedAnswers

  const loanCalcFinancials = {
    cashOut: cashOut ?? 0,
    totalFees: rate.loanEstimateSections['D'].total,
    totalEscrow: rate.loanEstimateSections['G'].total,
    lenderCredit: extractFeeByDescription(
      rate?.loanEstimateSections?.['J']?.fees,
      'Lender Credit'
    ),
    originationFee: extractFeeByDescription(
      rate?.loanEstimateSections?.['A'].fees,
      'Loan origination fee'
    ),
    taxAndPrepaids:
      rate.loanEstimateSections['E'].total +
      rate.loanEstimateSections['F'].total,
    remainingBalance:
      rate.loanPurpose === LoanPurpose.Refinance
        ? remainingBalance
        : propertyPurchasePrice - propertyDownPayment,
  }

  const loanCalcDecisions = {
    waiveEscrow: waiveEscrow === 'Yes',
    escrowRollIn: escrowRollIn === 'Yes',
    willRollInFees: willRollInFees === 'Yes',
  }

  const loanCostsCalculator = new LoanCostsCalculator(
    loanCalcFinancials,
    loanCalcDecisions
  )

  const loanCosts = loanCostsCalculator.getLoanCosts()

  return (
    <Modal className="fee-estimate">
      <div className="fee-estimate__close-button-wrap">
        <button
          className="fee-estimate__close-button"
          onClick={onClose}
          data-testid="fee-modal-close"
          type="button"
        >
          <img
            className="fee-estimate__close-button-icon"
            src={CloseIcon}
            alt="close icon"
          />
        </button>
      </div>

      {rate.loanEstimateSections ? (
        <React.Fragment>
          <section
            className="fee-estimate__main-section --fee-breakdown"
            data-testid="fee-modal"
          >
            <h3 className="fee-estimate__main-heading">
              Fee Breakdown
              <Tooltip
                text={
                  'The fee breakdown form is not a Loan Estimate (LE) required by federal law. If you make application with us, your LE will be sent to you in the opening package. When you have chosen to lock your interest rate, a revised LE will be sent to you. The fee breakdown is not a commitment to lend, nor is it a rate lock, pre-qualification or pre-approval. To fully evaluate your options, you should use the LE you will receive should you make application with us. This fee breakdown may not include any seller paid closing costs or additional settlement costs that could be incurred as part of this transaction such as HOA fees or assessments or the cost of a survey that may be required by a title insurance company.'
                }
                className="fee-estimate__tooltip"
              />
            </h3>

            <h4 className="fee-estimate__main-blurb">
              Your actual rate, payment, and costs could be higher. Get an
              official Loan Estimate before choosing a loan.
            </h4>

            <div className="fee-estimate__tabs">
              <button
                onClick={() => handleTabClick('cashToClose')}
                className={`fee-estimate__tab ${
                  activeTab === 'cashToClose' ? '--active' : ''
                }`}
              >
                Cash to close
              </button>
              <button
                onClick={() => handleTabClick('summaryCosts')}
                className={`fee-estimate__tab ${
                  activeTab === 'summaryCosts' ? '--active' : ''
                }`}
              >
                Summary of costs
              </button>
              <button
                onClick={() => handleTabClick('loanCosts')}
                className={`fee-estimate__tab ${
                  activeTab === 'loanCosts' ? '--active' : ''
                }`}
              >
                Loan costs
              </button>
              <button
                onClick={() => handleTabClick('otherCosts')}
                className={`fee-estimate__tab ${
                  activeTab === 'otherCosts' ? '--active' : ''
                }`}
              >
                Other costs
              </button>
            </div>

            {activeTab === 'loanCosts' ? (
              <div className="fee-estimate__section">
                <div className="fee-estimate__sub-section">
                  <p className="fee-estimate__sub-section-title">
                    Origination Costs:
                  </p>
                  {rate.loanEstimateSections['A'].fees.map((value, i) => (
                    <div className="fee-estimate__line-item" key={i}>
                      {value.description === 'Loan origination fee' ? (
                        <>
                          <p>
                            {value.description}
                            <Tooltip
                              text={
                                'This is lender or broker compensation for originating this loan and cannot be offset by lender credits.'
                              }
                            />
                          </p>
                          <p>{formatNumberToDollarAmount(value.amount)}</p>
                        </>
                      ) : (
                        <>
                          <p>{value.description}</p>
                          <p>{formatNumberToDollarAmount(value.amount)}</p>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="fee-estimate__sub-section">
                  <p className="fee-estimate__sub-section-title">
                    Services you cannot shop for:
                  </p>
                  {mapFeeSection(rate.loanEstimateSections['B'])}
                </div>

                <div className="fee-estimate__sub-section">
                  <p className="fee-estimate__sub-section-title">
                    Services you can shop for:
                  </p>
                  {mapFeeSection(rate.loanEstimateSections['C'])}
                </div>

                <div className="fee-estimate__section-total">
                  <h5>Total Loan Costs: </h5>
                  <h5>
                    {formatNumberToDollarAmount(
                      rate.loanEstimateSections['D'].total
                    )}
                  </h5>
                </div>
              </div>
            ) : null}

            {activeTab === 'otherCosts' ? (
              <div className="fee-estimate__section">
                <div className="fee-estimate__sub-section">
                  <p className="fee-estimate__sub-section-title">
                    Taxes and other government fees
                  </p>
                  {mapFeeSection(rate.loanEstimateSections['E'])}
                </div>

                <div className="fee-estimate__sub-section">
                  <p className="fee-estimate__sub-section-title">Prepaids</p>
                  {mapFeeSection(rate.loanEstimateSections['F'])}
                </div>

                <div className="fee-estimate__sub-section">
                  <p className="fee-estimate__sub-section-title">Escrows</p>
                  {rate.loanEstimateSections['G'].fees.map((value, i) => (
                    <div className="fee-estimate__line-item" key={i}>
                      <p>{value.description}</p>
                      <p>
                        {waiveEscrow === 'Yes'
                          ? '$0 (waived)'
                          : formatNumberToDollarAmount(value.amount)}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="fee-estimate__section-total">
                  <h5>Total Other Costs: </h5>
                  <h5>
                    {formatNumberToDollarAmount(
                      rate.loanEstimateSections['I'].total
                    )}
                  </h5>
                </div>
              </div>
            ) : null}

            {activeTab === 'summaryCosts' ? (
              <section className="fee-estimate__section">
                <div className="fee-estimate__section">
                  <div className="fee-estimate__sub-section">
                    <p className="fee-estimate__sub-section-title">
                      Total closing costs:
                    </p>
                    {mapFeeSection(rate.loanEstimateSections['J'])}
                  </div>

                  <div className="fee-estimate__section-total">
                    <h5>Total Closing Costs: </h5>
                    <h5>
                      {formatNumberToDollarAmount(loanCosts.finalClosingCosts)}
                    </h5>
                  </div>

                  {loanCosts.principalCurtailment ? (
                    <div className="fee-estimate__line-item">
                      <p>
                        Principal Curtailment
                        <Tooltip text="This credit is applied to your loan principal after closing." />
                      </p>
                      <p>
                        (
                        {formatNumberToDollarAmount(
                          loanCosts.principalCurtailment
                        )}
                        )
                      </p>
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}

            {activeTab === 'cashToClose' ? (
              <div className="fee-estimate__section">
                {rate.loanPurpose === LoanPurpose.Purchase ? (
                  <PurchaseClosingSection
                    propertyDownPayment={propertyDownPayment}
                    willRollInFees={willRollInFees}
                    loanCosts={loanCosts}
                    escrowRollIn={escrowRollIn}
                    waiveEscrow={waiveEscrow}
                    propertyPurchasePrice={propertyPurchasePrice}
                  />
                ) : (
                  <RefinanceClosingSection
                    remainingBalance={remainingBalance}
                    willRollInFees={willRollInFees}
                    loanCosts={loanCosts}
                    escrowRollIn={escrowRollIn}
                    waiveEscrow={waiveEscrow}
                    cashOut={cashOut}
                  />
                )}
              </div>
            ) : null}
          </section>
        </React.Fragment>
      ) : (
        <section
          className="fee-estimate__fee-breakdown"
          data-testid="fee-modal"
        >
          <h3 className="fee-estimate__main-heading">Fee Breakdown</h3>

          <h4 className="fee-estimate__main-blurb">
            An error occurred while pulling the fee breakdown.
          </h4>
        </section>
      )}
    </Modal>
  )
}

export default TabbedFeeEstimateModal

function mapFeeSection(section: Section): JSX.Element[] {
  return section.fees.map((value, i) => (
    <div className="fee-estimate__line-item" key={i}>
      <p>{value.description}</p>
      <p>{formatNumberToDollarAmount(value.amount)}</p>
    </div>
  ))
}

function RefinanceClosingSection({
  remainingBalance,
  willRollInFees,
  loanCosts,
  escrowRollIn,
  waiveEscrow,
  cashOut,
}): JSX.Element {
  return (
    <>
      <div className="fee-estimate__sub-section">
        <p className="fee-estimate__sub-section-title">Total cash to close</p>
        <div className="fee-estimate__line-item">
          <p>Remaining balance:</p>
          <p>{formatNumberToDollarAmount(remainingBalance)}</p>
        </div>

        {willRollInFees === 'Yes' ? (
          <>
            <div className="fee-estimate__line-item">
              <p>Financed Fees:</p>
              <p>
                {formatNumberToDollarAmount(
                  loanCosts.financedCostsDistribution.totalFees
                )}
              </p>
            </div>

            <div className="fee-estimate__line-item">
              <p>Financed Tax and Pre-Paids:</p>
              <p>
                {formatNumberToDollarAmount(
                  loanCosts.financedCostsDistribution.taxAndPrepaids
                )}
              </p>
            </div>
          </>
        ) : null}

        {escrowRollIn === 'Yes' && waiveEscrow !== 'Yes' ? (
          <div className="fee-estimate__line-item">
            <p>Escrow rolled in:</p>
            <p>
              {formatNumberToDollarAmount(
                loanCosts.financedCostsDistribution.totalEscrow +
                  loanCosts.financedCostsDistribution.taxAndPrepaids
              )}
            </p>
          </div>
        ) : null}

        {cashOut > 0 ? (
          <div className="fee-estimate__line-item">
            <p>Cash out amount:</p>
            <p>{formatNumberToDollarAmount(cashOut)}</p>
          </div>
        ) : null}

        <div className="fee-estimate__line-item">
          <p>Locked loan amount</p>
          <p>({formatNumberToDollarAmount(loanCosts.lockedLoanAmount)})</p>
        </div>
      </div>

      <div className="fee-estimate__section-total">
        <h5>Total cash to close:</h5>
        <h5>{formatNumberToDollarAmount(loanCosts.cashToClose)}</h5>
      </div>

      <div className="fee-estimate__sub-section-info">
        <InfoBlock>
          The total amount of money you would pay today to refinance with this
          rate is{' '}
          <span className="fee-estimate__cash-info">
            {formatNumberToDollarAmount(loanCosts.cashToClose)}
          </span>
        </InfoBlock>
      </div>
    </>
  )
}

function PurchaseClosingSection({
  propertyPurchasePrice,
  propertyDownPayment,
  willRollInFees,
  loanCosts,
  escrowRollIn,
  waiveEscrow,
}): JSX.Element {
  return (
    <>
      <div className="fee-estimate__sub-section">
        <p className="fee-estimate__sub-section-title">Total cash to close</p>
        <div className="fee-estimate__line-item">
          <p>Purchase Price:</p>
          <p>{formatNumberToDollarAmount(propertyPurchasePrice)}</p>
        </div>

        {willRollInFees === 'Yes' ? (
          <div className="fee-estimate__line-item">
            <p>Financed Fees:</p>
            <p>
              {formatNumberToDollarAmount(
                loanCosts.financedCostsDistribution.totalFees
              )}
            </p>
          </div>
        ) : null}

        {escrowRollIn === 'Yes' && waiveEscrow !== 'Yes' ? (
          <div className="fee-estimate__line-item">
            <p>Escrow rolled in:</p>
            <p>
              {formatNumberToDollarAmount(
                loanCosts.financedCostsDistribution.totalEscrow +
                  loanCosts.financedCostsDistribution.taxAndPrepaids
              )}
            </p>
          </div>
        ) : null}

        <div className="fee-estimate__line-item">
          <p>Down Payment:</p>
          <p>{formatNumberToDollarAmount(propertyDownPayment)}</p>
        </div>

        <div className="fee-estimate__line-item">
          <p>Locked loan amount</p>
          <p>({formatNumberToDollarAmount(loanCosts.lockedLoanAmount)})</p>
        </div>
      </div>

      <div className="fee-estimate__section-total">
        <h5>Total cash to close:</h5>
        <h5>
          {formatNumberToDollarAmount(
            loanCosts.cashToClose + propertyDownPayment
          )}
        </h5>
      </div>

      <div className="fee-estimate__sub-section-info">
        <InfoBlock>
          The total amount of money you would pay today to purchase with this
          rate is{' '}
          <span className="fee-estimate__cash-info">
            {formatNumberToDollarAmount(
              loanCosts.cashToClose + propertyDownPayment
            )}
          </span>
        </InfoBlock>
      </div>
    </>
  )
}
